/* Shared-image motion is separate from the gallery's directional navigation. */
window.NeutralsImageViewer = (dialog, { signal, reduced, currentFrame, destinations, settle, focusReturn }) => {
  let phase = 'closed', token = 0, clone, veil, unlock = () => {};
  const animations = new Set(), timers = new Map();
  const surface = dialog.querySelector('[data-viewer-images]');
  const controls = [dialog.querySelector('[data-close]'), dialog.querySelector('.np__viewer-controls')];
  const easing = 'cubic-bezier(.22,1,.36,1)';
  function duration() {
    return reduced.matches ? 100 : parseFloat(getComputedStyle(dialog).getPropertyValue('--np-viewer-duration')) || 220;
  }
  function bounds(image, intrinsic = image) {
    if (!image?.isConnected) return null;
    const box = image.getBoundingClientRect();
    if (!box.width || !box.height || !intrinsic.naturalWidth) return null;
    // Both existing galleries use contain: measure the photograph, not its letterbox.
    const scale = Math.min(box.width / intrinsic.naturalWidth, box.height / intrinsic.naturalHeight);
    const width = intrinsic.naturalWidth * scale, height = intrinsic.naturalHeight * scale;
    return {left:box.left + (box.width-width)/2,top:box.top + (box.height-height)/2,width,height};
  }
  function visible(element) {
    const box = element?.getBoundingClientRect();
    return element?.isConnected && box?.width > 0 && box.height > 0 && box.right > 0 && box.bottom > 0 && box.left < innerWidth && box.top < innerHeight;
  }
  function matchingDestination() {
    const id = currentFrame()?.dataset.mediaId;
    const matches = destinations().filter(element => element.dataset.mediaId === id);
    return matches.find(element => visible(element) && visible(element.querySelector('img'))) || null;
  }
  function lock() {
    const html = document.documentElement, body = document.body;
    const saved = [html.style.overflow, body.style.paddingRight];
    const gap = innerWidth - html.clientWidth;
    if (gap) body.style.paddingRight = `${parseFloat(getComputedStyle(body).paddingRight) + gap}px`;
    html.style.overflow = 'hidden';
    unlock = () => { html.style.overflow = saved[0]; body.style.paddingRight = saved[1]; unlock = () => {}; };
  }
  function wait(ms) {
    return new Promise(resolve => { const id = setTimeout(() => { timers.delete(id); resolve(); }, ms); timers.set(id, resolve); });
  }
  async function animate(element, frames, options) {
    if (element.animate) {
      const animation = element.animate(frames, {...options, fill:'both'});
      animations.add(animation);
      await Promise.race([animation.finished.catch(() => {}), wait(options.duration + (options.delay || 0) + 100)]);
      return;
    }
    // CSS fallback retains the same transforms without requiring the Web Animations API.
    Object.assign(element.style, frames[0]);
    element.getBoundingClientRect();
    element.style.transition = `transform ${options.duration}ms ${easing} ${options.delay || 0}ms, opacity ${options.duration}ms ${easing} ${options.delay || 0}ms`;
    Object.assign(element.style, frames[frames.length-1]);
    await wait(options.duration + (options.delay || 0) + 100);
  }
  function clear() {
    animations.forEach(animation => animation.cancel()); animations.clear();
    timers.forEach((resolve,id) => { clearTimeout(id); resolve(); }); timers.clear();
    clone?.remove(); clone = null; veil?.remove(); veil = null;
    surface.style.visibility = '';
    [dialog, ...controls].forEach(element => {
      element.style.removeProperty('opacity'); element.style.removeProperty('transition');
      element.style.removeProperty('background-color');
    });
    delete dialog.dataset.motion;
  }
  function backdrop() {
    veil = document.createElement('div');
    veil.className = 'np__motion-backdrop'; veil.setAttribute('aria-hidden','true');
    dialog.append(veil); dialog.style.backgroundColor = 'transparent';
    return veil;
  }
  function makeClone(image, rect) {
    const element = document.createElement('img');
    element.src = image.currentSrc || image.src;
    element.alt = ''; element.setAttribute('aria-hidden','true');
    element.className = 'np__flight';
    Object.assign(element.style, {left:`${rect.left}px`,top:`${rect.top}px`,width:`${rect.width}px`,height:`${rect.height}px`});
    dialog.append(element); clone = element;
    return element;
  }
  const transform = (from,to) => `translate(${to.left-from.left}px, ${to.top-from.top}px) scale(${to.width/from.width})`;
  async function open(trigger) {
    if (phase !== 'closed' || signal.aborted) return;
    const source = trigger.querySelector('img'), start = bounds(source);
    if (!start) return;
    const run = ++token;
    phase = 'opening'; lock(); focusReturn(trigger);
    dialog.dataset.motion = 'opening';
    dialog.showModal();
    dialog.focus({preventScroll:true});
    const image = currentFrame().querySelector('img');
    image.loading = 'eager';
    // Keep the already-loaded source until the high-resolution image decodes.
    let loaded = image.complete && !!image.naturalWidth;
    image.decode?.().then(() => { loaded = !!image.naturalWidth; }).catch(() => {});
    const target = bounds(image, source) || {left:24,top:70,width:innerWidth-48,height:(innerWidth-48)*start.height/start.width};
    const flight = makeClone(source, reduced.matches ? target : start);
    surface.style.visibility = 'hidden';
    const ms = duration();
    await Promise.all([
      animate(backdrop(),[{opacity:0},{opacity:1}],{duration:ms,easing}),
      animate(flight,reduced.matches ? [{opacity:0},{opacity:1}] : [{transform:'none'},{transform:transform(start,target)}],{duration:ms,easing}),
      ...controls.map(control => animate(control,[{opacity:0},{opacity:1}],{duration:Math.min(140,ms),delay:reduced.matches ? 0 : ms-140,easing}))
    ]);
    if (run !== token) return;
    if (!loaded) {
      image.removeAttribute('srcset'); image.src = source.currentSrc || source.src;
    }
    clear(); phase = 'open';
    dialog.querySelector('[data-close]').focus({preventScroll:true});
  }
  async function close() {
    // A quick second tap can request close before the opening flight finishes.
    // Cancel and fully clean that interrupted state so iOS cannot retain the veil.
    if (phase === 'opening') {
      token++; clear(); unlock(); phase = 'closed';
      if (dialog.open) dialog.close();
      return;
    }
    if (phase !== 'open') return;
    phase = 'closing'; const run = ++token;
    settle();
    const frame = currentFrame(), image = frame.querySelector('img');
    const destination = matchingDestination();
    const start = bounds(image), end = bounds(destination?.querySelector('img'));
    if (destination) focusReturn(destination);
    dialog.dataset.motion = 'closing';
    const ms = duration();
    const tasks = [animate(backdrop(),[{opacity:1},{opacity:0}],{duration:ms,easing}),
      ...controls.map(control => animate(control,[{opacity:1},{opacity:0}],{duration:Math.min(100,ms),easing}))];
    if (start) {
      const flight = makeClone(image,start); surface.style.visibility = 'hidden';
      tasks.push(animate(flight,reduced.matches ? [{opacity:1},{opacity:0}] : end ? [{transform:'none'},{transform:transform(start,end)}] : [{opacity:1,transform:'none'},{opacity:0,transform:'scale(.98)'}],{duration:ms,easing}));
    }
    await Promise.all(tasks);
    if (run !== token) return;
    dialog.close(); clear(); unlock(); phase = 'closed';
  }
  function destroy() {
    token++; clear(); if (dialog.open) dialog.close(); unlock(); phase = 'closed';
  }
  signal.addEventListener('abort',destroy,{once:true});
  document.addEventListener('visibilitychange', () => { if (document.hidden && phase !== 'closed') destroy(); }, {signal});
  dialog.addEventListener('close', () => { if (phase !== 'closed') { token++; clear(); unlock(); phase = 'closed'; } }, {signal});
  dialog.addEventListener('click', event => {
    if (event.target.closest('button')) return;
    const rect = bounds(currentFrame()?.querySelector('img'));
    if (!rect || event.clientX < rect.left || event.clientX > rect.left+rect.width || event.clientY < rect.top || event.clientY > rect.top+rect.height) close();
  }, {signal});
  return {open,close,destroy,get busy(){return phase === 'opening' || phase === 'closing';}};
};
